import {Component, Input, OnChanges, OnDestroy, ViewChild} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {MatLegacyPaginator as MatPaginator} from '@angular/material/legacy-paginator';
import {Observable, Subscription, forkJoin, of} from 'rxjs';
import {catchError, map, shareReplay, switchMap} from 'rxjs/operators';
import {EnvService} from '../services/env.service';

@Component({
  standalone: false,
  selector: 'micro-cmdb-cluster-nes',
  templateUrl: './cmdb-cluster-nes.component.html'
})
export class CmdbClusterNesComponent implements OnChanges, OnDestroy {
  @Input() clusterId: string;
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
  nes: any[] = [];
  loading = false;
  loadFailed = false;
  private request?: Subscription;
  private records = new Map<string, Observable<any>>();

  constructor(private http: HttpClient, private env: EnvService) {}

  ngOnChanges(): void {
    this.paginator.pageIndex = 0;
    this.records.clear();
    this.reload();
  }

  reload(): void {
    this.request?.unsubscribe();
    this.nes = [];
    this.paginator.length = 0;
    this.loadFailed = false;
    this.loading = false;
    // Never issue an unfiltered request for an unsaved cluster.
    if (!this.clusterId) {
      return;
    }
    this.loading = true;
    const params = new HttpParams()
      .set('clusterId', this.clusterId)
      .set('page', String(this.paginator.pageIndex || 0))
      .set('size', String(this.paginator.pageSize || 20));
    this.request = this.http.get<any>(`${this.env.e.url}/domain/nes`, {params}).pipe(
      switchMap(page => {
        this.paginator.pageIndex = page.number;
        this.paginator.pageSize = page.size;
        this.paginator.length = page.totalElements;
        const rows = page.content.map(ne => forkJoin({
          neTypeRecord: this.record('neTypes', ne.neTypeId),
          classCode: this.record('cmdbClassCodes', ne.classCodeId),
          applicationTag: this.record('cmdbApplicationTags', ne.applicationTagId),
          osFamily: this.record('cmdbOsVersions', ne.osVersionId).pipe(
            switchMap(version => this.record('cmdbOsFamily', version?.osFamilyId))
          )
        }).pipe(map(details => ({...ne, ...details}))));
        return rows.length ? forkJoin(rows) : of([]);
      })
    ).subscribe({
      next: (nes: any[]) => {
        this.nes = nes;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.loadFailed = true;
      }
    });
  }

  private record(resource: string, id: string): Observable<any> {
    if (!id) {
      return of(null);
    }
    const key = `${resource}/${id}`;
    if (!this.records.has(key)) {
      this.records.set(key, this.http.get<any>(`${this.env.e.url}/domain/${key}`).pipe(
        catchError(() => of(null)),
        shareReplay({bufferSize: 1, refCount: true})
      ));
    }
    return this.records.get(key)!;
  }

  ngOnDestroy(): void {
    this.request?.unsubscribe();
  }
}
